import React, { useState, useEffect, useRef } from 'react';
import {
  Package, ArrowDownCircle, ArrowUpCircle, Plus, RotateCcw, Search,
  ChevronDown, X, CheckCircle
} from 'lucide-react';
import { Technician, DarkStore, TechnicianStockItem, TechnicianStockTransaction, ServiceType } from '../types';
import apiService from '../services/api';
import toast from 'react-hot-toast';

// ─── Searchable select used for service picker ─────────────────────────────
interface SearchableOption { id: number; label: string; }

const SearchableSelect: React.FC<{
  options: SearchableOption[];
  value: number | null;
  onChange: (id: number | null) => void;
  placeholder: string;
  disabled?: boolean;
}> = ({ options, value, onChange, placeholder, disabled }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const selected = options.find(o => o.id === value);
  const filtered = options.filter(o => o.label.toLowerCase().includes(q.toLowerCase()));

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
    else setQ('');
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <div
        onClick={() => !disabled && setOpen(!open)}
        className={`flex items-center justify-between px-3 py-2 text-sm border rounded-lg cursor-pointer ${
          disabled ? 'bg-gray-50 cursor-not-allowed text-gray-400' : 'bg-white border-gray-200 hover:border-emerald-400'
        }`}
      >
        <span className={selected ? 'text-gray-900' : 'text-gray-400'}>{selected?.label ?? placeholder}</span>
        <div className="flex items-center gap-1">
          {selected && !disabled && (
            <button onMouseDown={e => { e.stopPropagation(); onChange(null); }}
              className="text-gray-400 hover:text-gray-600"><X className="h-3 w-3" /></button>
          )}
          <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </div>
      </div>
      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
          <div className="p-2 border-b border-gray-100">
            <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)}
              placeholder="Search..." className="w-full text-sm px-2 py-1 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500" />
          </div>
          <div className="max-h-52 overflow-y-auto">
            {filtered.length === 0
              ? <div className="px-3 py-2 text-sm text-gray-400">No results</div>
              : filtered.map(o => (
                <div key={o.id} onClick={() => { onChange(o.id); setOpen(false); }}
                  className={`px-3 py-2 text-sm cursor-pointer hover:bg-emerald-50 ${o.id === value ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-gray-700'}`}>
                  {o.label}
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Async product search select ───────────────────────────────────────────
const AsyncProductSelect: React.FC<{
  value: number | null;
  onChange: (id: number | null, item: ServiceType | null) => void;
}> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState<ServiceType[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<ServiceType | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await apiService.getProducts(1, 30, q);
        setResults(res?.entity?.items ?? []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
  }, [q, open]);

  const handleSelect = (item: ServiceType) => {
    setSelected(item);
    onChange(item.id, item);
    setOpen(false);
    setQ('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelected(null);
    onChange(null, null);
  };

  const label = selected ? `${selected.makeName} ${selected.modelName} — ${selected.serviceName}` : null;

  return (
    <div ref={ref} className="relative">
      <div onClick={() => setOpen(!open)}
        className="flex items-center justify-between px-3 py-2 text-sm border border-gray-200 rounded-lg cursor-pointer bg-white hover:border-emerald-400">
        <span className={label ? 'text-gray-900 truncate pr-2' : 'text-gray-400'}>{label ?? 'Search product…'}</span>
        <div className="flex items-center gap-1 flex-shrink-0">
          {selected && (
            <button onMouseDown={handleClear} className="text-gray-400 hover:text-gray-600"><X className="h-3 w-3" /></button>
          )}
          <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </div>
      </div>
      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
          <div className="p-2 border-b border-gray-100">
            <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)}
              placeholder="Type brand, model or service…"
              className="w-full text-sm px-2 py-1 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500" />
          </div>
          <div className="max-h-56 overflow-y-auto">
            {loading
              ? <div className="px-3 py-3 text-sm text-gray-400 text-center">Searching…</div>
              : results.length === 0
                ? <div className="px-3 py-3 text-sm text-gray-400 text-center">{q ? 'No results' : 'Start typing to search'}</div>
                : results.map(item => (
                  <div key={item.id} onMouseDown={() => handleSelect(item)}
                    className={`px-3 py-2 text-sm cursor-pointer hover:bg-emerald-50 ${item.id === value ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-gray-700'}`}>
                    <span className="font-medium">{item.makeName} {item.modelName}</span>
                    <span className="text-gray-500"> — {item.serviceName}</span>
                  </div>
                ))
            }
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Issue Modal ───────────────────────────────────────────────────────────
interface IssueModalProps {
  technicians: Technician[];
  darkStores: DarkStore[];
  defaultTechnicianId?: number;
  onClose: () => void;
  onSuccess: () => void;
}

const IssueModal: React.FC<IssueModalProps> = ({ technicians, darkStores, defaultTechnicianId, onClose, onSuccess }) => {
  const [technicianId, setTechnicianId] = useState<number | null>(defaultTechnicianId ?? null);
  const [serviceId, setServiceId] = useState<number | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [darkStoreId, setDarkStoreId] = useState<number | null>(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [availableQty, setAvailableQty] = useState<number | null>(null);
  const [loadingQty, setLoadingQty] = useState(false);
  const [techStockQty, setTechStockQty] = useState<number | null>(null);
  const [loadingTechStock, setLoadingTechStock] = useState(false);

  const techOptions = technicians.map(t => ({ id: t.id, label: t.name }));

  // Fetch available qty from dark store inventory when service + dark store are both selected
  useEffect(() => {
    if (!serviceId || !darkStoreId) {
      setAvailableQty(null);
      return;
    }
    setLoadingQty(true);
    apiService.getInventory({ darkStoreId, serviceId, pageSize: 1 })
      .then((data: any) => {
        const items = data?.items ?? data ?? [];
        const found = Array.isArray(items) ? items.find((i: any) => i.serviceId === serviceId) : null;
        setAvailableQty(found ? found.quantity : 0);
      })
      .catch(() => setAvailableQty(null))
      .finally(() => setLoadingQty(false));
  }, [serviceId, darkStoreId]);

  // Fetch technician's current stock when technician + service are both selected
  useEffect(() => {
    if (!technicianId || !serviceId) {
      setTechStockQty(null);
      return;
    }
    setLoadingTechStock(true);
    apiService.getTechnicianStock(technicianId)
      .then((items: any[]) => {
        const found = items.find((i: any) => i.serviceId === serviceId);
        setTechStockQty(found ? found.quantity : 0);
      })
      .catch(() => setTechStockQty(null))
      .finally(() => setLoadingTechStock(false));
  }, [technicianId, serviceId]);

  const handleSubmit = async () => {
    if (!technicianId || !serviceId || quantity <= 0) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (darkStoreId && availableQty !== null && quantity > availableQty) {
      toast.error(`Only ${availableQty} unit(s) available in the selected dark store`);
      return;
    }
    setSaving(true);
    try {
      const result = await apiService.issueTechnicianStock({
        technicianId,
        serviceId,
        quantity,
        darkStoreId: darkStoreId ?? null,
        notes: notes || undefined,
      });
      if (result.whatsappWarning) {
        toast.success('Product issued');
        toast.error(`WhatsApp: ${result.whatsappWarning}`, { duration: 6000 });
      } else {
        toast.success('Product issued & WhatsApp sent to technician');
      }
      onSuccess();
      onClose();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to issue product');
    } finally {
      setSaving(false);
    }
  };

  const stockBadge = () => {
    if (!darkStoreId || !serviceId) return null;
    if (loadingQty) return <span className="text-xs text-gray-400 animate-pulse">Checking stock…</span>;
    if (availableQty === null) return null;
    if (availableQty === 0) return <span className="text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded-full">Out of stock</span>;
    if (availableQty <= 5) return <span className="text-xs font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">Low stock · {availableQty} left</span>;
    return <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">{availableQty} in stock</span>;
  };

  const techStockBadge = () => {
    if (!technicianId || !serviceId) return null;
    if (loadingTechStock) return <span className="text-xs text-gray-400 animate-pulse">Checking tech stock…</span>;
    if (techStockQty === null) return null;
    if (techStockQty === 0) return <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">Tech has: 0</span>;
    return <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">Tech already has: {techStockQty}</span>;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
              <ArrowDownCircle className="h-4 w-4 text-emerald-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Issue Product to Technician</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Row 1: Technician + Dark Store */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Technician *</label>
              <SearchableSelect options={techOptions} value={technicianId} onChange={setTechnicianId} placeholder="Select technician" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Dark Store (From)</label>
              <select value={darkStoreId ?? ''} onChange={e => setDarkStoreId(e.target.value ? Number(e.target.value) : null)}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500">
                <option value="">— None —</option>
                {darkStores.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
          </div>

          {/* Row 2: Product (wide) + Quantity (narrow) */}
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-medium text-gray-600">Product / Service *</label>
                <div className="flex items-center gap-2">
                  {techStockBadge()}
                  {stockBadge()}
                </div>
              </div>
              <AsyncProductSelect
                value={serviceId}
                onChange={(id, _item) => setServiceId(id)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Quantity *</label>
              <input
                type="number" min={1}
                max={availableQty !== null && darkStoreId ? availableQty : undefined}
                value={quantity}
                onChange={e => setQuantity(Number(e.target.value))}
                className={`w-full text-sm border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                  darkStoreId && availableQty !== null && quantity > availableQty
                    ? 'border-red-400 bg-red-50'
                    : 'border-gray-200'
                }`}
              />
              {darkStoreId && availableQty !== null && quantity > availableQty && (
                <p className="text-xs text-red-600 mt-1">Exceeds stock ({availableQty})</p>
              )}
            </div>
          </div>

          {/* Row 3: Notes + WhatsApp hint */}
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Notes</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none" />
            </div>
            <div className="flex items-end">
              <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg w-full">
                <CheckCircle className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-blue-700">WhatsApp will be sent to the technician on issue.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose} className="px-5 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
          <button onClick={handleSubmit} disabled={saving || (darkStoreId !== null && availableQty === 0)}
            className="flex-1 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-lg hover:from-emerald-700 hover:to-emerald-800 disabled:opacity-50">
            {saving ? 'Issuing...' : 'Issue & Notify'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Return Modal ──────────────────────────────────────────────────────────
interface ReturnModalProps {
  item: TechnicianStockItem;
  darkStores: DarkStore[];
  onClose: () => void;
  onSuccess: () => void;
}

const ReturnModal: React.FC<ReturnModalProps> = ({ item, darkStores, onClose, onSuccess }) => {
  const [quantity, setQuantity] = useState(1);
  const [restockDarkStoreId, setRestockDarkStoreId] = useState<number | null>(item.darkStoreId ?? null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (quantity <= 0 || quantity > item.quantity) {
      toast.error(`Quantity must be between 1 and ${item.quantity}`);
      return;
    }
    setSaving(true);
    try {
      await apiService.returnTechnicianStock({
        technicianId: item.technicianId,
        serviceId: item.serviceId,
        quantity,
        restockDarkStoreId: restockDarkStoreId ?? null,
        notes: notes || undefined,
      });
      toast.success('Return recorded successfully');
      onSuccess();
      onClose();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to record return');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
              <ArrowUpCircle className="h-4 w-4 text-orange-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Return Product</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="p-3 bg-gray-50 rounded-lg text-sm">
            <p className="font-medium text-gray-900">{item.makeName} {item.modelName}</p>
            <p className="text-gray-600">{item.serviceName}</p>
            <p className="text-gray-500 text-xs mt-1">With: {item.technicianName} · Current qty: <strong>{item.quantity}</strong></p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Quantity to Return *</label>
            <input type="number" min={1} max={item.quantity} value={quantity}
              onChange={e => setQuantity(Number(e.target.value))}
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Restock to Dark Store</label>
            <select value={restockDarkStoreId ?? ''} onChange={e => setRestockDarkStoreId(e.target.value ? Number(e.target.value) : null)}
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500">
              <option value="">— Don't restock —</option>
              {darkStores.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none" />
          </div>
        </div>

        <div className="flex gap-3 p-5 border-t border-gray-100">
          <button onClick={onClose} className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
          <button onClick={handleSubmit} disabled={saving}
            className="flex-1 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg hover:from-orange-600 hover:to-orange-700 disabled:opacity-50">
            {saving ? 'Saving...' : 'Record Return'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ─────────────────────────────────────────────────────────────
const TechnicianStockPage: React.FC = () => {
  const [tab, setTab] = useState<'stock' | 'transactions'>('stock');
  const [stock, setStock] = useState<TechnicianStockItem[]>([]);
  const [transactions, setTransactions] = useState<TechnicianStockTransaction[]>([]);
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [darkStores, setDarkStores] = useState<DarkStore[]>([]);
  const [filterTechId, setFilterTechId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [returnItem, setReturnItem] = useState<TechnicianStockItem | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadMeta();
  }, []);

  useEffect(() => {
    if (tab === 'stock') loadStock();
    else loadTransactions();
  }, [tab, filterTechId]);

  const loadMeta = async () => {
    try {
      const [techRes, dsRes] = await Promise.all([
        apiService.getTechnicians(),
        apiService.getDarkStores(),
      ]);
      if (techRes.isSuccess) setTechnicians(techRes.entity ?? []);
      setDarkStores(Array.isArray(dsRes) ? dsRes : (dsRes as any)?.entity ?? []);
    } catch {}
  };

  const loadStock = async () => {
    setLoading(true);
    try {
      const data = await apiService.getTechnicianStock(filterTechId ?? undefined);
      setStock(data);
    } catch {
      toast.error('Failed to load stock');
    } finally {
      setLoading(false);
    }
  };

  const loadTransactions = async () => {
    setLoading(true);
    try {
      const data = await apiService.getTechnicianStockTransactions(filterTechId ?? undefined, undefined, 200);
      setTransactions(data);
    } catch {
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const filteredStock = stock.filter(s =>
    !search || s.serviceName.toLowerCase().includes(search.toLowerCase()) ||
    s.technicianName.toLowerCase().includes(search.toLowerCase()) ||
    s.makeName.toLowerCase().includes(search.toLowerCase()) ||
    s.modelName.toLowerCase().includes(search.toLowerCase())
  );

  const filteredTransactions = transactions.filter(t =>
    !search || t.serviceName.toLowerCase().includes(search.toLowerCase()) ||
    t.technicianName.toLowerCase().includes(search.toLowerCase()) ||
    (t.orderNumber ?? '').toLowerCase().includes(search.toLowerCase())
  );

  const techOptions = technicians.map(t => ({ id: t.id, label: t.name }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-violet-600 rounded-lg flex items-center justify-center">
            <Package className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Technician Stock</h1>
            <p className="text-sm text-gray-500">Track parts issued to and returned by technicians</p>
          </div>
        </div>
        <button
          onClick={() => setShowIssueModal(true)}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-lg hover:from-emerald-700 hover:to-emerald-800"
        >
          <Plus className="h-4 w-4 mr-2" />
          Issue Product
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-end">
          <div className="w-56">
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Filter by Technician</label>
            <SearchableSelect options={techOptions} value={filterTechId} onChange={setFilterTechId} placeholder="All technicians" />
          </div>
          <div className="flex-1 max-w-sm">
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Product, technician..."
                className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500" />
            </div>
          </div>
          <button onClick={() => tab === 'stock' ? loadStock() : loadTransactions()}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200">
            <RotateCcw className="h-4 w-4" /> Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex border-b border-gray-200">
          <button onClick={() => setTab('stock')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${tab === 'stock' ? 'border-emerald-500 text-emerald-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            <div className="flex items-center gap-2">
              <ArrowDownCircle className="h-4 w-4" />
              Current Stock {stock.length > 0 && <span className="bg-emerald-100 text-emerald-700 text-xs px-1.5 py-0.5 rounded-full">{stock.length}</span>}
            </div>
          </button>
          <button onClick={() => setTab('transactions')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${tab === 'transactions' ? 'border-emerald-500 text-emerald-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            <div className="flex items-center gap-2">
              <ArrowUpCircle className="h-4 w-4" />
              Transactions
            </div>
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
          </div>
        ) : tab === 'stock' ? (
          filteredStock.length === 0 ? (
            <div className="text-center py-16">
              <Package className="mx-auto h-12 w-12 text-gray-300 mb-3" />
              <p className="text-gray-500">No stock currently with technicians</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Technician</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dark Store</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Issued</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {filteredStock.map(item => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{item.technicianName}</div>
                        <div className="text-xs text-gray-500">{item.technicianMobile}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">{item.serviceName}</div>
                        <div className="text-xs text-gray-500">{item.makeName} {item.modelName}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-semibold bg-violet-100 text-violet-800">
                          {item.quantity}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {item.orderNumber ?? <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {item.darkStoreName ?? <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                        {new Date(item.updatedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        <button onClick={() => setReturnItem(item)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-orange-600 bg-orange-50 rounded-lg hover:bg-orange-100">
                          <ArrowUpCircle className="h-3.5 w-3.5" /> Return
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : (
          filteredTransactions.length === 0 ? (
            <div className="text-center py-16">
              <Package className="mx-auto h-12 w-12 text-gray-300 mb-3" />
              <p className="text-gray-500">No transactions found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Technician</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Balance</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {filteredTransactions.map(txn => (
                    <tr key={txn.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                        {new Date(txn.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}{' '}
                        {new Date(txn.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{txn.technicianName}</td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900">{txn.serviceName}</div>
                        <div className="text-xs text-gray-500">{txn.makeName} {txn.modelName}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
                          txn.transactionType === 'ISSUED'
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-orange-100 text-orange-700'
                        }`}>
                          {txn.transactionType === 'ISSUED'
                            ? <ArrowDownCircle className="h-3 w-3" />
                            : <ArrowUpCircle className="h-3 w-3" />}
                          {txn.transactionType}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{txn.quantity}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {txn.quantityBefore} → <strong className="text-gray-900">{txn.quantityAfter}</strong>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {txn.orderNumber ?? <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">
                        {txn.notes ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>

      {showIssueModal && (
        <IssueModal
          technicians={technicians}
          darkStores={darkStores}
          onClose={() => setShowIssueModal(false)}
          onSuccess={loadStock}
        />
      )}

      {returnItem && (
        <ReturnModal
          item={returnItem}
          darkStores={darkStores}
          onClose={() => setReturnItem(null)}
          onSuccess={loadStock}
        />
      )}
    </div>
  );
};

export default TechnicianStockPage;
